import Sample from 'repos/Sample.js';
import Tone from 'tone';
import StepData from "pattern/StepData.js";
import {sleep} from 'utils/sleep.js';
import SamplesStream from './SamplesStream.js';
import {
    setTrackBrowsedSamples,
    setSampleAuditioning,
    setSample,
    setTrackIsLoading
} from 'actions/index.js';
 
const DEFAULT_STEP_DATA = new StepData();
export default superclass => class extends superclass  {
    backStreams = [];
    forwardStreams = [];

    newSampleStream(samplesStream) {
        if (this.samplesStream) {
            this.backStreams.push(this.samplesStream);
        }
        this.forwardStreams.forEach(s => s.dispose());
        this.forwardStreams = [];
        this.samplesStream = samplesStream;
    }

    backSamplesStream() {
        if (this.backStreams.length === 0) {
            return;
        }
        let samplesStream = this.backStreams.pop();
        this.forwardStreams = [this.samplesStream, ... this.forwardStreams];
        this.samplesStream =  samplesStream;
    }

    forwardSamplesStream() {
        let samplesStream = this.forwardStreams.shift();
        this.backStreams.push(this.samplesStream);
        this.samplesStream = samplesStream;
    }
    
    getSamplesStream() {
        return this.samplesStream;
    }

    async auditionNext(shouldDispose=true, isKeyboard=false) {
        if (this.isFrozen) {
            return;
        }

        let trackNumber = this.instrumentRack ? this.instrumentRack.trackNumber: this.trackNumber;
        this.store.dispatch(setTrackIsLoading(this.subSequence.id, trackNumber, true));

        let dateA = new Date().getTime();
        try {
            if (this.loadingAudition) {
                this.store.dispatch(setTrackIsLoading(this.subSequence.id, trackNumber, false));
                return;
            }
            let samplesStream = this.getSamplesStream();
            for (let i=0; i < 8 && !samplesStream.hasNext(); i++) {
                if (i === 7) {
                    this.store.dispatch(setTrackIsLoading(this.subSequence.id, trackNumber, false));
                    return;
                }
                await sleep(200);
            }
            this.loadingAudition = true;
            let sample = await samplesStream.getNext();
            this.store.dispatch(setTrackIsLoading(this.subSequence.id, trackNumber, false));
            this.loadingAudition = false;
            this.releaseAll();
            this.useNewSound(sample);
            DEFAULT_STEP_DATA.track = this;
            DEFAULT_STEP_DATA.duration = 4;
            if (!this.sequencerEngine.playing) {
                this.trigger(DEFAULT_STEP_DATA);
            }
            this.addSoundToBrowsedSamples(sample);
            let dateB = new Date().getTime();
            let time = dateB - dateA;
        } catch (e) {
            this.loadingAudition = false;
        }
    }

    async auditionPrevious(shouldDispose=true, isKeyboard=false) {
        if (this.isFrozen) {
            return;
        }
        let trackNumber = this.instrumentRack ? this.instrumentRack.trackNumber: this.trackNumber;
        this.store.dispatch(setTrackIsLoading(this.subSequence.id, trackNumber, true));
        try {
            let samplesStream = this.getSamplesStream();
            if (this.loadingAudition) {
                this.store.dispatch(setTrackIsLoading(this.subSequence.id, trackNumber, false));
                return;
            }
            if (!samplesStream.hasPrevious()) {
                this.store.dispatch(setTrackIsLoading(this.subSequence.id, trackNumber, false));
                return;
            }
            this.loadingAudition = true;
            let sample = await samplesStream.getPrevious();
            this.store.dispatch(setTrackIsLoading(this.subSequence.id, trackNumber, false));
            this.loadingAudition = false;
            this.addSoundToBrowsedSamples(sample);
            this.releaseAll();
            await this.useNewSound(sample);
            DEFAULT_STEP_DATA.track = this;
            DEFAULT_STEP_DATA.duration = 4;
            if (!this.sequencerEngine.playing) {
                this.trigger(DEFAULT_STEP_DATA);
            }
        } catch (e) {
            this.loadingAudition = false;
        }
    }



    useSound(sample, shouldDispose) {
        // dispose every sampler
        if (shouldDispose) {
            for (let i in this.samplers) {
                this.samplers[i].dispose();
                delete this.samplers[i];
            }
        }
        this.sample = sample;
        this.soundIds = [sample.getId()];
        this.toAudition.push(sample.getId());
        this.loadTrackInfo(true, sample);
    }

    useNewSound(sample, play=false) {
        let oldId = Object.keys(this.samplers)[0];
        let sampler = this.samplers[oldId];
        if (!sampler) {
            this.useSound(sample, true);
            return;
        }
        this.samplers[sample.getId()] = sampler;
        delete this.samplers[oldId];
        sampler.sample = sample;
        this.soundIds = [sample.getId()];
        this.videoId = sample.videoId;
        this.sample = sample;
        if (this.videoId === undefined && this.getTrackType() !== 'MIDI') {
            this.name = sample.name;
        } else {
            this.name = sample.name;
        }
 
        sampler.replaceBuffer(sample.audioBuffer);

        if (play) {
            DEFAULT_STEP_DATA.track = this;
            DEFAULT_STEP_DATA.duration = 4;
            if (!this.sequencerEngine.playing) {
                this.trigger(DEFAULT_STEP_DATA);
            }
        }
    }

    async auditionSeek(index) {
        this.store.dispatch(setTrackIsLoading(this.subSequence.id, this.trackNumber, true));
        let sample = await this.samplesStream.seek(index);
        if (!sample) {
            this.store.dispatch(setTrackIsLoading(this.subSequence.id, this.trackNumber, false));
            return;
        }
        this.loadingAudition = false;
        this.addSoundToBrowsedSamples(sample);
        this.releaseAll();
        await this.useNewSound(sample);
        DEFAULT_STEP_DATA.track = this;
        DEFAULT_STEP_DATA.duration = 4;
        if (!this.sequencerEngine.playing) {
            this.trigger(DEFAULT_STEP_DATA);
        }
        this.store.dispatch(setTrackIsLoading(this.subSequence.id, this.trackNumber, false));
    }

    async auditionSound(sound, shouldDispose=false, samplesStream, forward=true) {
        this.store.dispatch(setTrackIsLoading(this.subSequence.id, this.trackNumber, true));

        let currentSample = this.sample;

        if (typeof sound === "string") {
            sound = new Sample(sound);
        }
        if (!sound.isLoaded()) {
            await sound.load();
            if (sound.samplesStream) {
                samplesStream = sound.samplesStream.getCopy(sound);
            }
        }
        this.savedSoundIds = [...this.soundIds];
        this.soundIds = [sound.getId()];
        this.toAudition.push(sound.getId());
        this.auditionedSounds.push(sound.getId());
        if (samplesStream) {
            samplesStream.counter = samplesStream.sounds.indexOf(sound.getId());
        }
        if (this.samplesStream && shouldDispose) {
            this.samplesStream.dispose();
        }
        this.samplesStream = samplesStream ? samplesStream :
            new SamplesStream(sound, this.sequencerEngine);
        this.loadTrackInfo(true, sound);

        if (forward) {
            this.addSoundToBrowsedSamples(sound, currentSample);
 
       }
    }

    addSoundToBrowsedSamples(sound, currentSample) {
        let browsedSamples = this.state.tracks.trackBrowsedSamples[this.id] || [];
        if (currentSample) {
            let i = browsedSamples.indexOf(currentSample);
            if (i > -1) {
                let samplesToDispose = browsedSamples.slice(0, i);
                samplesToDispose.forEach(
                    s => {
                        let {ipfsHash} = s;
                        let sampler = this.samplers[ipfsHash];
                        if (sampler) {
                            sampler.dispose();
                            delete this.samplers[ipfsHash];
                        }
                        s.dispose(true);
                    });
                browsedSamples = browsedSamples.slice(i);
            }
        }

        if (browsedSamples.indexOf(sound) > -1) {
            // no need to update list since its already in it
            return;
        }

        let samples =
            [
                sound,
                ... (browsedSamples.filter(s => s.ipfsHash !== sound.ipfsHash))
            ];

        this.store.dispatch(setTrackBrowsedSamples(this.id, samples));
    }

    isAuditioning() {
        return this.auditionedSounds.length > 0;
    }

    completeAudition() {
        for (let i in this.auditionedSounds) {
            if (this.soundIds.indexOf(this.auditionedSounds[i]) == -1) {
                let s = this.samplers[this.auditionedSounds[i]];
                if (s) {
                    s.dispose();
                    delete this.samplers[this.auditionedSounds[i]];
                }
            }
        }

        this.auditionedSounds = [];

        this.store.dispatch(setSampleAuditioning(undefined));
    }

    cancelAudition() {
        if (this.isAuditioning()) {
            this.soundIds = this.savedSoundIds;
            this.revertAuditionedSounds();
        }
        this.store.dispatch(setSampleAuditioning(undefined));
    }

    revertAuditionedSounds() {
        for (let i in this.auditionedSounds) {
            let sampler = this.samplers[this.auditionedSounds[i]];
            if (sampler) {
                sampler.dispose();
                delete this.samplers[this.auditionedSounds[i]];
            }
        }

        this.auditionedSounds = [];
    }
}
